import React, { useState } from 'react';
import { processMobileDeposit } from '../../utils/firestore';
import { useAuth } from '../../contexts/AuthContext';

export default function MobileDeposit({ accounts }) {
  const { currentUser } = useAuth();
  const [selectedAccount, setSelectedAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    if (!selectedAccount || !amount || !frontImage || !backImage) {
      console.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    const depositAmount = parseFloat(amount);
    if (isNaN(depositAmount) || depositAmount <= 0) {
      console.error('Please enter a valid amount');
      setLoading(false);
      return;
    }

    if (depositAmount > 10000) {
      console.error('Mobile deposits are limited to $10,000 per check');
      setLoading(false);
      return;
    }

    try {
      await processMobileDeposit({
        toAccount: selectedAccount,
        amount: depositAmount,
        frontImage,
        backImage,
        userId: currentUser.uid
      });

      setSuccess(true);
      setLoading(false);
      setSelectedAccount('');
      setAmount('');
      setFrontImage(null);
      setBackImage(null);
    } catch (error) {
      console.error('Deposit failed. Please try again.', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const ImageUploadBox = ({ label, image, setImage, id }) => (
    <div className="relative">
      <input
        type="file"
        id={id}
        className="hidden"
        accept="image/*"
        onChange={(e) => setImage(e.target.files[0])}
        disabled={loading}
      />
      <label
        htmlFor={id}
        className={`block w-full aspect-video border-2 border-dashed rounded-xl 
          ${image ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 bg-gray-50'} 
          hover:border-emerald-500 hover:bg-emerald-50 transition-colors duration-200 cursor-pointer`}
      >
        <div className="flex flex-col items-center justify-center h-full p-6">
          {image ? (
            <>
              <span className="text-3xl mb-2">✓</span>
              <span className="text-sm font-medium text-emerald-600">Image uploaded</span>
              <span className="text-xs text-gray-500 mt-1">{image.name}</span>
            </>
          ) : (
            <>
              <span className="text-3xl mb-2">📸</span>
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <span className="text-xs text-gray-500 mt-1">Click to upload</span>
            </>
          )}
        </div>
      </label>
    </div>
  );

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mobile Deposit</h3>
      
      {success && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 text-primary-700 rounded-xl flex items-center">
          <span className="text-xl mr-2">✓</span>
          Check deposit submitted successfully! Your funds will be available according to our standard deposit policy.
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deposit To
          </label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 hover:bg-white transition-colors duration-200"
            required
            disabled={loading}
          >
            <option value="">Select account</option>
            {accounts.map((account) => (
              <option key={account.type} value={account.type}>
                {account.type} (*{account.accountNumber.slice(-4)}) - ${account.balance}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full p-3 pl-8 border border-gray-200 rounded-xl focus:ring-primary-500 focus:border-primary-500 bg-gray-50 hover:bg-white transition-colors duration-200"
              placeholder="0.00"
              min="0.01"
              step="0.01"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploadBox
            label="Upload Check Front"
            image={frontImage}
            setImage={setFrontImage}
            id="front-image"
          />
          <ImageUploadBox
            label="Upload Check Back"
            image={backImage}
            setImage={setBackImage}
            id="back-image"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !amount || !frontImage || !backImage}
          className={`w-full py-3 px-4 rounded-xl text-white font-medium
            ${loading || !amount || !frontImage || !backImage
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            } transition-colors duration-200`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Processing...
            </div>
          ) : (
            'Deposit Check'
          )}
        </button>

        <div className="mt-6 bg-gray-50 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Important Notes:</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Ensure check is signed and endorsed
            </li>
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Write "For Mobile Deposit Only" below signature
            </li>
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Images must be clear and well-lit
            </li>
          </ul>
        </div>
      </form>
    </div>
  );
} 